import { Link } from "react-router-dom";
import { AlertTriangle, Users } from "lucide-react";
import StatusPill from "./StatusPill";
import { timeAgo } from "../lib/format";
import type { EmployeeSummary } from "../lib/types";

export default function EmployeeList({ items }: { items: EmployeeSummary[] }) {
  if (items.length === 0) {
    return <div className="text-sm text-slate-400 px-4 py-8 text-center">No employees match the current filters.</div>;
  }

  // Group by current.country
  const groups = new Map<string, EmployeeSummary[]>();
  for (const emp of items) {
    const key = emp.current.country || emp.home_country || "Unknown";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(emp);
  }
  const orderedGroups = Array.from(groups.entries()).sort((a, b) => b[1].length - a[1].length);

  return (
    <div className="space-y-4">
      {orderedGroups.map(([country, list]) => (
        <div key={country}>
          <div className="text-xs uppercase tracking-wide text-slate-500 px-2 mb-1.5">
            {country} · <span className="text-slate-400">{list.length}</span>
          </div>
          <ul className="space-y-1">
            {list.map((emp) => (
              <li key={emp.oracle_id}>
                <Link
                  to={`/employees/${encodeURIComponent(emp.oracle_id)}`}
                  className="flex items-center gap-3 px-2.5 py-2 rounded-lg hover:bg-slate-800/60 transition"
                >
                  <Avatar name={emp.full_name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-slate-100 truncate">{emp.full_name}</div>
                      {emp.red_flag_count > 0 && (
                        <span
                          className="relative inline-flex group"
                          onClick={(e) => e.preventDefault()}
                        >
                          <AlertTriangle size={13} className="text-amber-400" />
                          <span
                            role="tooltip"
                            className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1 z-20 hidden group-hover:block min-w-[180px] max-w-[280px] rounded-md bg-slate-900 ring-1 ring-amber-400/40 px-2 py-1.5 text-[11px] text-amber-100 shadow-lg whitespace-pre-line"
                          >
                            {(emp.red_flags && emp.red_flags.length > 0
                              ? emp.red_flags
                              : [`${emp.red_flag_count} red flag(s)`]).join("\n")}
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {emp.title || emp.org_function || "—"} · {emp.nationality || "—"}
                    </div>
                  </div>
                  <div className="text-right">
                    <StatusPill status={emp.current.status} />
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1 justify-end">
                      {emp.family_count > 1 && (
                        <span className="inline-flex items-center gap-0.5">
                          <Users size={11} /> {emp.family_count}
                        </span>
                      )}
                      <span>· {timeAgo(emp.current.last_seen_at)}</span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
  return (
    <div className="w-8 h-8 rounded-full bg-slate-800 ring-1 ring-slate-700 flex items-center justify-center text-xs text-slate-300 font-medium">
      {initials || "?"}
    </div>
  );
}
